import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfileIATAPage.css';
import './PaymentPorterPage.css';
import pmmService from '../services/paymentMigrationManagerService';
import { useAccount } from '../contexts/AccountContext';
import { formatDate } from '../utils/datetime';

const PMMIcon = () => (
    <div className="pi-avatar" style={{ background: '#f0fdf4' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17l10 5 10-5" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12l10 5 10-5" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </div>
);

const PaymentMigrationManagerListPage = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, selectedHotel } = useAccount();
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await pmmService.list({
                hotel_id:   selectedHotel   || undefined,
                account_id: !selectedHotel && selectedAccount  ? selectedAccount  : undefined,
                partner_id: !selectedHotel && !selectedAccount && selectedPartner ? selectedPartner : undefined,
            });
            setItems(res.data?.items || []);
        } catch (err) {
            console.error('Failed to load Payment Migration Managers', err);
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, selectedPartner, selectedHotel]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Delete this Payment Migration Manager?')) return;
        try {
            await pmmService.remove(id);
            setItems(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error('Failed to delete PMM', err);
        }
    };

    const filtered = items.filter(p =>
        !search ||
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.hotel_name || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Apps
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Payment Migration Manager</h1>
                        <p className="pi-subtitle">Apply payment actions to reservations across a date range</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pp-refresh-btn" onClick={load} disabled={loading}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                    <button className="pi-btn-add" onClick={() => navigate('/apps/payment-migration-manager/new')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        New PMM
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search by name, description or hotel..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="pi-list-panel">
                {filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M2 17l10 5 10-5" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M2 12l10 5 10-5" stroke="#d1d5db" strokeWidth="1.5"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No Payment Migration Managers match your search' : 'No Payment Migration Managers configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by creating your first Payment Migration Manager.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Hotel</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr
                                    key={p.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/apps/payment-migration-manager/${p.id}`)}
                                >
                                    <td><PMMIcon /></td>
                                    <td><strong>{p.name}</strong></td>
                                    <td>{p.description || '—'}</td>
                                    <td>{p.hotel_name || '—'}</td>
                                    <td>
                                        <span className={`pp-status-badge ${p.active ? 'status-active' : 'status-inactive'}`}>
                                            {p.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>{formatDate(p.created_at)}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button
                                                className="pi-action-btn"
                                                onClick={e => { e.stopPropagation(); navigate(`/apps/payment-migration-manager/${p.id}/run`); }}
                                                title="Run"
                                            >
                                                Run
                                            </button>
                                            <button
                                                className="pi-action-btn pi-action-btn--danger"
                                                onClick={e => handleDelete(e, p.id)}
                                                title="Delete"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PaymentMigrationManagerListPage;
